// src/pages/RulesetRequest.jsx
// T178 Ruleset Access Request — LRP/MRP only

import React, { useState, useEffect } from 'react';
import { permissions, riskTypeCategories } from '../data/permissionCatalog';
import { lookupManager, checkLeaderLevel } from '../services/api';
import { getCurrentUser } from '../services/auth';

// Filter only LRP and MRP permissions
const rulesetPermissions = permissions.filter(p => p.riskType === 'LRP' || p.riskType === 'MRP');
const lrpPermissions = rulesetPermissions.filter(p => p.riskType === 'LRP');
const mrpPermissions = rulesetPermissions.filter(p => p.riskType === 'MRP');

const APPROVAL_TEMPLATE_ID = '49028';

// Vertical Stepper Component
const VerticalStepper = ({ step }) => {
    const steps = [
        { num: 1, label: 'Setup', desc: 'Team & config' },
        { num: 2, label: 'Permissions', desc: 'Select access' },
        { num: 3, label: 'Details', desc: 'Rule & form' },
        { num: 4, label: 'Leader', desc: 'L7 approval' },
        { num: 5, label: 'Submit', desc: 'Review & send' }
    ];

    const currentIdx = steps.findIndex(s => s.num >= step);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            {steps.map((s, idx) => {
                const isCompleted = idx < currentIdx;
                const isCurrent = idx === currentIdx;
                return (
                    <div key={s.num} style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{
                                width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '14px', fontWeight: '700', flexShrink: 0,
                                background: isCompleted ? '#22c55e' : isCurrent ? '#ff9900' : '#e9ecef',
                                color: isCompleted ? '#fff' : isCurrent ? '#000' : '#999',
                                border: isCurrent ? '3px solid #ffcc80' : '2px solid transparent'
                            }}>
                                {isCompleted ? '✓' : idx + 1}
                            </div>
                            {idx < steps.length - 1 && (
                                <div style={{ width: '2px', height: '28px', background: isCompleted ? '#22c55e' : '#e2e6ea', margin: '4px 0' }} />
                            )}
                        </div>
                        <div style={{ paddingTop: '6px' }}>
                            <div style={{ fontSize: '14px', fontWeight: isCurrent ? '700' : '500', color: isCurrent ? '#111' : isCompleted ? '#333' : '#999' }}>{s.label}</div>
                            <div style={{ fontSize: '12px', color: isCurrent ? '#555' : '#aaa', marginTop: '1px' }}>{s.desc}</div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};


const RulesetRequest = ({ userAlias }) => {
    const [step, setStep] = useState(1);
    
    // Step 1: Setup (auto-populated)
    const [userDepartment, setUserDepartment] = useState('');
    const [userJobFamily, setUserJobFamily] = useState('');
    const [isDepartmentLoading, setIsDepartmentLoading] = useState(false);

    // Step 2: Permissions
    const [selectedPermissions, setSelectedPermissions] = useState(new Set());
    const [collapsedLRP, setCollapsedLRP] = useState(false);
    const [collapsedMRP, setCollapsedMRP] = useState(false);

    // Step 3: Details
    const [ruleDetail, setRuleDetail] = useState('');
    const [reportsOf, setReportsOf] = useState('');
    const [usersLogins, setUsersLogins] = useState('');
    const [toolsRequested, setToolsRequested] = useState('');
    const [costCenter, setCostCenter] = useState('');
    const [jobFamily, setJobFamily] = useState('');
    const [jobCode, setJobCode] = useState('');
    const [useCase, setUseCase] = useState('');

    // Step 4: Leader
    const [leaderAlias, setLeaderAlias] = useState('');
    const [leaderData, setLeaderData] = useState(null);
    const [isLookingUpLeader, setIsLookingUpLeader] = useState(false);
    const [leaderError, setLeaderError] = useState('');
    // Manual leader level-check state: { checking, status: 'ok'|'too_low'|'not_found'|'error', jobLevel }
    const [manualCheck, setManualCheck] = useState({});
    const [manualLeader, setManualLeader] = useState(false);
    const [manualLeaderInput, setManualLeaderInput] = useState('');

    // Step 5: Submit
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(null);
    const [submitError, setSubmitError] = useState('');

    // Auto-detect department on mount
    useEffect(() => {
        const loadDepartment = async () => {
            if (!userAlias || isDepartmentLoading) return;
            setIsDepartmentLoading(true);
            try {
                const result = await lookupManager(userAlias, 6);
                if (result && result.userDepartment) setUserDepartment(result.userDepartment);
                if (result && result.userJobFamily) setUserJobFamily(result.userJobFamily);
            } catch (err) {
                console.warn('Department lookup failed:', err);
            } finally {
                setIsDepartmentLoading(false);
            }
        };
        loadDepartment();
    }, [userAlias]);

    // L7 Leader lookup
    const lookupL7Leader = async () => {
        setIsLookingUpLeader(true);
        setLeaderError('');
        try {
            const result = await lookupManager(userAlias, 7);
            if (result && result.managerChain) {
                const l7 = result.managerChain.find(m => m.jobLevel && m.jobLevel >= 7);
                if (l7) {
                    setLeaderData(l7);
                    setLeaderAlias(l7.alias);
                } else {
                    setLeaderError('Could not find L7+ manager. Please enter manually.');
                    setManualLeader(true);
                }
            } else if (result && result.l8Manager) {
                setLeaderData(result.l8Manager);
                setLeaderAlias(result.l8Manager.alias);
            } else {
                setLeaderError('Could not auto-detect leader. Please enter manually.');
                setManualLeader(true);
            }
        } catch (err) {
            setLeaderError('Lookup failed: ' + err.message);
            setManualLeader(true);
        } finally {
            setIsLookingUpLeader(false);
        }
    };

    const togglePermission = (permId) => {
        setSelectedPermissions(prev => {
            const next = new Set(prev);
            if (next.has(permId)) next.delete(permId);
            else next.add(permId);
            return next;
        });
    };

    const selectedArray = Array.from(selectedPermissions);
    const selectedObjects = rulesetPermissions.filter(p => selectedPermissions.has(p.id));

    // ════════════════════════════════════════
    // RENDER
    // ════════════════════════════════════════
    return (
        <div style={{ width: '100%', padding: '0 40px 96px', maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ marginBottom: '16px' }}>
                <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111', margin: '0 0 2px 0' }}>📋 T178 Ruleset Access Request</h1>
                <p style={{ fontSize: '13px', color: '#666', margin: 0 }}>Request rule-based access for Low Risk and Medium Risk permissions</p>
            </div>

            <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
                {/* Stepper */}
                <div style={{ width: '170px', flexShrink: 0 }}>
                    <div style={{ position: 'sticky', top: '72px' }}>
                        <VerticalStepper step={step} />
                    </div>
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>

                    {/* ══════ STEP 1: Setup ══════ */}
                    {step === 1 && (
                        <div>
                            <div style={{ padding: '12px 16px', borderRadius: '8px', background: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: '12px', fontSize: '14px' }}>
                                <span style={{ fontSize: '15px' }}>👤</span> <strong>Requester: {userAlias}</strong> — Authenticated via Midway
                            </div>
                            <div style={{ padding: '12px 16px', borderRadius: '8px', background: '#f0f8ff', border: '1px solid #b8d4f0', marginBottom: '12px', fontSize: '14px' }}>
                                <span style={{ fontSize: '15px' }}>🏢</span> <strong>Department:</strong> {isDepartmentLoading ? '⏳ Loading...' : (userDepartment || '—')} | <strong>Job Family:</strong> {userJobFamily || '—'}
                            </div>
                            <div style={{ padding: '12px 16px', borderRadius: '8px', background: '#f8f9fa', border: '1px solid #e2e6ea', marginBottom: '16px', fontSize: '14px' }}>
                                <span style={{ fontSize: '15px' }}>🏷</span> <strong>Tenant/Platform:</strong> <span style={{ padding: '2px 10px', borderRadius: '6px', background: '#e2e6ea', color: '#555', fontWeight: '600' }}>T178</span> <span style={{ fontSize: '12px', color: '#999' }}>(pre-filled)</span>
                            </div>
                            <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#fffbeb', border: '1px solid #fde68a', fontSize: '13px', color: '#92400e', marginBottom: '16px' }}>
                                ⚠️ Ruleset Access is available only for Low Risk (LRP) and Medium Risk (MRP) permissions. For HRP/CRP, please use Individual Permissions Access.
                            </div>
                            <button type="button" onClick={() => setStep(2)}
                                style={{ width: '100%', padding: '14px', borderRadius: '8px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', border: 'none', background: '#ff9900', color: '#000' }}>
                                Continue to Permissions →
                            </button>
                        </div>
                    )}

                    {/* ══════ STEP 2: Permissions ══════ */}
                    {step === 2 && (
                        <div>
                            <button type="button" onClick={() => setStep(1)}
                                style={{ padding: '6px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', border: '1px solid #ddd', background: '#fff', color: '#666', marginBottom: '16px' }}>
                                ← Back to Setup
                            </button>

                            {/* LRP Section */}
                            <div style={{ marginBottom: '16px' }}>
                                <div onClick={() => setCollapsedLRP(!collapsedLRP)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', borderRadius: '8px', background: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: '8px' }}>
                                    <span>{collapsedLRP ? '▶' : '▼'}</span>
                                    <span style={{ fontSize: '15px' }}>🟢</span>
                                    <strong style={{ color: '#166534' }}>Low Risk Permissions (LRP)</strong>
                                    <span style={{ fontSize: '12px', color: '#666' }}>({lrpPermissions.length})</span>
                                    <span style={{ fontSize: '12px', color: '#666', marginLeft: 'auto' }}>180 days</span>
                                </div>
                                {!collapsedLRP && lrpPermissions.map(perm => (
                                    <div key={perm.id} onClick={() => togglePermission(perm.id)}
                                        style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', background: selectedPermissions.has(perm.id) ? '#f0fdf4' : '#fff' }}>
                                        <input type="checkbox" checked={selectedPermissions.has(perm.id)} readOnly style={{ width: '16px', height: '16px' }} />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: '600', fontSize: '14px', color: '#111' }}>{perm.name} <span style={{ fontSize: '11px', color: '#666', fontWeight: '400' }}>L6</span>
                                                {perm.restrictions && <span style={{ marginLeft: '8px', padding: '1px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600', background: '#fff3cd', color: '#92400e', border: '1px solid #fde68a' }}>⚠️ {perm.restrictions}</span>}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>{perm.description}</div>
                                        </div>
                                        <a href={perm.permissionLink} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ fontSize: '12px', color: '#0073bb', textDecoration: 'none' }}>View ↗</a>
                                    </div>
                                ))}
                            </div>

                            {/* MRP Section */}
                            <div style={{ marginBottom: '16px' }}>
                                <div onClick={() => setCollapsedMRP(!collapsedMRP)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', borderRadius: '8px', background: '#e0f2fe', border: '1px solid #7dd3fc', marginBottom: '8px' }}>
                                    <span>{collapsedMRP ? '▶' : '▼'}</span>
                                    <span style={{ fontSize: '15px' }}>🔵</span>
                                    <strong style={{ color: '#0c4a6e' }}>Medium Risk Permissions (MRP)</strong>
                                    <span style={{ fontSize: '12px', color: '#666' }}>({mrpPermissions.length})</span>
                                    <span style={{ fontSize: '12px', color: '#666', marginLeft: 'auto' }}>180 days</span>
                                </div>
                                {!collapsedMRP && mrpPermissions.map(perm => (
                                    <div key={perm.id} onClick={() => togglePermission(perm.id)}
                                        style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', background: selectedPermissions.has(perm.id) ? '#e0f2fe' : '#fff' }}>
                                        <input type="checkbox" checked={selectedPermissions.has(perm.id)} readOnly style={{ width: '16px', height: '16px' }} />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: '600', fontSize: '14px', color: '#111' }}>{perm.name} <span style={{ fontSize: '11px', color: '#666', fontWeight: '400' }}>L6</span></div>
                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>{perm.description}</div>
                                        </div>
                                        <a href={perm.permissionLink} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ fontSize: '12px', color: '#0073bb', textDecoration: 'none' }}>View ↗</a>
                                    </div>
                                ))}
                            </div>

                            {/* Selected count + Continue */}
                            {selectedPermissions.size > 0 && (
                                <div style={{ position: 'sticky', bottom: '16px', padding: '12px 16px', borderRadius: '8px', background: '#fff', border: '1px solid #22c55e', boxShadow: '0 -2px 10px rgba(0,0,0,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#166534' }}>✅ {selectedPermissions.size} permission(s) selected</span>
                                    <button type="button" onClick={() => setStep(3)}
                                        style={{ padding: '10px 24px', borderRadius: '8px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', border: 'none', background: '#ff9900', color: '#000' }}>
                                        Continue to Details →
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ══════ STEP 3: Details ══════ */}
                    {step === 3 && (
                        <div>
                            <button type="button" onClick={() => setStep(2)}
                                style={{ padding: '6px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', border: '1px solid #ddd', background: '#fff', color: '#666', marginBottom: '16px' }}>
                                ← Back to Permissions
                            </button>

                            <div style={{ fontSize: '15px', fontWeight: '700', color: '#111', marginBottom: '16px' }}>📝 Ruleset Details</div>

                            {/* Rule Detail - moved up as it drives user list */}
                            <div style={{ marginBottom: '14px' }}>
                                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Rule Detail <span style={{ color: '#d13212' }}>*</span></label>
                                <textarea value={ruleDetail} onChange={(e) => setRuleDetail(e.target.value)}
                                    placeholder="Describe the rule to apply (e.g., 'All reports of abhimige in OEX CTT team should have Base PI access')"
                                    rows={3} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }} />
                            </div>

                            {/* Reports Of */}
                            <div style={{ marginBottom: '14px' }}>
                                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Reports of (specify if only direct reports)</label>
                                <input type="text" value={reportsOf} onChange={(e) => setReportsOf(e.target.value)}
                                    placeholder="e.g., abhimige (direct reports only)" style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }} />
                            </div>

                            {/* Users Logins */}
                            <div style={{ marginBottom: '14px' }}>
                                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Users Logins (optional)</label>
                                <textarea value={usersLogins} onChange={(e) => setUsersLogins(e.target.value)}
                                    placeholder="Enter user logins (comma or newline separated) — or leave blank if rule covers all"
                                    rows={2} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }} />
                            </div>

                            {/* Tools/Functions Requested — pre-filled from selected permissions */}
                            <div style={{ marginBottom: '14px' }}>
                                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Tool(s)/Function(s) Requested <span style={{ color: '#d13212' }}>*</span></label>
                                <input type="text" value={toolsRequested || selectedObjects.map(p => p.name).join(', ')} onChange={(e) => setToolsRequested(e.target.value)}
                                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', background: '#f8f9fa' }} />
                                <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>Auto-filled from selected permissions. Edit if needed.</div>
                            </div>

                            {/* Cost Center */}
                            <div style={{ marginBottom: '14px' }}>
                                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Cost Center (Phonetool) <span style={{ color: '#d13212' }}>*</span></label>
                                <input type="text" value={costCenter} onChange={(e) => setCostCenter(e.target.value)}
                                    placeholder="e.g., 1234" style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }} />
                            </div>

                            {/* Job Family + Job Code in 2 columns */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                                <div>
                                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Job Family <span style={{ color: '#d13212' }}>*</span></label>
                                    <input type="text" value={jobFamily} onChange={(e) => setJobFamily(e.target.value)}
                                        placeholder="e.g., PRGMGT" style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }} />
                                </div>
                                <div>
                                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Job Code <span style={{ color: '#d13212' }}>*</span></label>
                                    <input type="text" value={jobCode} onChange={(e) => setJobCode(e.target.value)}
                                        placeholder="e.g., P04" style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }} />
                                </div>
                            </div>

                            {/* Use Case */}
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ fontSize: '13px', fontWeight: '600', color: '#333', display: 'block', marginBottom: '4px' }}>Use-Case <span style={{ color: '#d13212' }}>*</span></label>
                                <textarea value={useCase} onChange={(e) => setUseCase(e.target.value)}
                                    placeholder="Type your use case here following the template below..."
                                    rows={4} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }} />
                                {!useCase.trim() && (
                                    <div style={{ marginTop: '8px', padding: '10px 14px', borderRadius: '6px', background: '#f8f9fa', border: '1px solid #e9ecef', fontSize: '12px', color: '#888', lineHeight: '1.7', fontStyle: 'italic' }}>
                                        <strong style={{ color: '#666', fontStyle: 'normal' }}>Template:</strong> As an <strong>[job family(s)/job title]</strong> working in <strong>[org]</strong> <strong>[function/process]</strong> I need to use this permission <strong>[insert action]</strong> to look up/take action <strong>[what information are you focusing on - fields/widgets/etc]</strong>. I use this information to <strong>[X - what do you do with the information, decisions, tools, processes]</strong>. Without this access <strong>[Y - what would be the impact if you did not have this access - quantify monetary loss, productivity loss, etc]</strong>
                                    </div>
                                )}
                            </div>

                            {/* Continue */}
                            {ruleDetail.trim() && (toolsRequested.trim() || selectedObjects.length > 0) && costCenter.trim() && jobFamily.trim() && jobCode.trim() && useCase.trim() && (
                                <button type="button" onClick={() => setStep(4)}
                                    style={{ width: '100%', padding: '14px', borderRadius: '8px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', border: 'none', background: '#ff9900', color: '#000' }}>
                                    Continue to Leader →
                                </button>
                            )}
                        </div>
                    )}

                    {/* ══════ STEP 4: Leader ══════ */}
                    {step === 4 && (
                        <div>
                            <button type="button" onClick={() => setStep(3)}
                                style={{ padding: '6px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', border: '1px solid #ddd', background: '#fff', color: '#666', marginBottom: '16px' }}>
                                ← Back to Details
                            </button>

                            <div style={{ fontSize: '15px', fontWeight: '700', color: '#111', marginBottom: '8px' }}>👔 Leadership Approval (L7+)</div>
                            <div style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>Ruleset access requires L7+ manager approval.</div>

                            <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#fef2f2', border: '1px solid #fecaca', marginBottom: '14px', fontSize: '13px', color: '#991b1b', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                <span style={{ fontSize: '16px', lineHeight: '1' }}>⚠️</span>
                                <span><strong>Important:</strong> Please ensure you select the correct leader for approval. If the wrong leader is selected, your request will be rejected.</span>
                            </div>

                            {!leaderAlias && !manualLeader && (
                                <button type="button" onClick={lookupL7Leader} disabled={isLookingUpLeader}
                                    style={{ width: '100%', padding: '12px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', border: 'none', background: '#ff9900', color: '#000', marginBottom: '12px' }}>
                                    {isLookingUpLeader ? '⏳ Looking up L7 manager...' : '🔍 Auto-detect L7 Manager'}
                                </button>
                            )}

                            {!leaderAlias && !manualLeader && (
                                <button type="button" onClick={() => setManualLeader(true)}
                                    style={{ width: '100%', padding: '10px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', border: '2px solid #fde68a', background: '#fffbeb', color: '#92400e' }}>
                                    ✏️ Or Enter Manager Manually
                                </button>
                            )}

                            {leaderError && <div style={{ padding: '8px 12px', borderRadius: '6px', background: '#fffbeb', border: '1px solid #fde68a', fontSize: '12px', color: '#92400e', marginBottom: '12px' }}>⚠️ {leaderError}</div>}

                            {leaderAlias && (
                                <div style={{ padding: '12px 16px', borderRadius: '8px', background: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div>
                                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#166534' }}>✅ {leaderAlias} {leaderData?.jobLevel ? `(L${leaderData.jobLevel})` : ''}</span>
                                        {leaderData?.title && <div style={{ fontSize: '11px', color: '#555' }}>{leaderData.title}</div>}
                                    </div>
                                    <button type="button" onClick={() => { setLeaderAlias(''); setLeaderData(null); setManualLeader(true); setManualLeaderInput(''); setManualCheck({}); setLeaderError(''); }}
                                        style={{ fontSize: '11px', color: '#0073bb', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Change</button>
                                </div>
                            )}

                            {manualLeader && !leaderAlias && (
                                <div style={{ marginBottom: '12px' }}>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <input type="text" value={manualLeaderInput}
                                            onChange={(e) => { setManualLeaderInput(e.target.value.toLowerCase().trim()); setManualCheck({}); }}
                                            placeholder="Enter L7+ manager alias"
                                            style={{ flex: 1, padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }} />
                                        <button type="button"
                                            onClick={async () => {
                                                const alias = manualLeaderInput.trim();
                                                if (!alias) return;
                                                setManualCheck({ checking: true });
                                                const res = await checkLeaderLevel(alias, 7, userAlias); // Ruleset = L7 manager
                                                if (res.status === 'self') {
                                                    setManualCheck({ status: 'self' });
                                                } else if (res.status === 'ok') {
                                                    setLeaderAlias(alias);
                                                    setLeaderData({ alias, jobLevel: res.jobLevel, title: '(manual)', source: 'manual' });
                                                    setManualCheck({ status: 'ok' });
                                                } else if (res.status === 'not_found') {
                                                    setLeaderAlias(alias);
                                                    setLeaderData({ alias, jobLevel: null, title: '(manual — unverified)', source: 'manual', unverified: true });
                                                    setManualCheck({ status: 'not_found' });
                                                } else {
                                                    setManualCheck({ status: res.status, jobLevel: res.jobLevel });
                                                }
                                            }}
                                            disabled={!manualLeaderInput.trim() || manualCheck.checking}
                                            style={{ padding: '10px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: manualCheck.checking ? 'wait' : 'pointer', border: 'none', background: '#ff9900', color: '#000' }}>
                                            {manualCheck.checking ? 'Checking...' : 'Set'}
                                        </button>
                                    </div>
                                    {manualCheck.status === 'self' && (
                                        <div style={{ marginTop: '6px', padding: '8px 12px', borderRadius: '6px', background: '#ffeef0', border: '1px solid #ffc1c8', fontSize: '12px', color: '#d13212' }}>
                                            ❌ You cannot select yourself as your approval leader. Please enter a different leader at or above the required level.
                                        </div>
                                    )}
                                    {manualCheck.status === 'too_low' && (
                                        <div style={{ marginTop: '6px', padding: '8px 12px', borderRadius: '6px', background: '#ffeef0', border: '1px solid #ffc1c8', fontSize: '12px', color: '#d13212' }}>
                                            ❌ Leader submitted by you is <strong>below the required level</strong>. Ruleset requires an L7+ leader, but <strong>{manualLeaderInput}</strong> is L{manualCheck.jobLevel}. Please enter a valid L7+ leader.
                                        </div>
                                    )}
                                    {manualCheck.status === 'error' && (
                                        <div style={{ marginTop: '6px', padding: '8px 12px', borderRadius: '6px', background: '#fff3cd', border: '1px solid #ffe69c', fontSize: '12px', color: '#856404' }}>
                                            ⚠️ Could not verify the leader right now. Please try again.
                                        </div>
                                    )}
                                </div>
                            )}
                            {manualCheck.status === 'not_found' && leaderAlias && (
                                <div style={{ marginBottom: '12px', padding: '8px 12px', borderRadius: '6px', background: '#fffbeb', border: '1px solid #fde68a', fontSize: '12px', color: '#92400e' }}>
                                    ⚠️ Leader <strong>{leaderAlias}</strong> is not synced into our portal. You can proceed and submit, but ensure the leader is at or above <strong>L7</strong>, otherwise the request will be rejected.
                                </div>
                            )}

                            {leaderAlias && (
                                <button type="button" onClick={() => setStep(5)}
                                    style={{ width: '100%', padding: '14px', borderRadius: '8px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', border: 'none', background: '#ff9900', color: '#000', marginTop: '12px' }}>
                                    Continue to Review →
                                </button>
                            )}
                        </div>
                    )}

                    {/* ══════ STEP 5: Submit ══════ */}
                    {step === 5 && (
                        <div>
                            <button type="button" onClick={() => setStep(4)}
                                style={{ padding: '6px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', border: '1px solid #ddd', background: '#fff', color: '#666', marginBottom: '16px' }}>
                                ← Back to Leader
                            </button>

                            <div style={{ fontSize: '15px', fontWeight: '700', color: '#111', marginBottom: '16px' }}>📋 Review & Submit</div>

                            {/* Summary */}
                            <div style={{ padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#f8fafc', marginBottom: '16px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
                                    <div><strong>Requester:</strong> {userAlias}</div>
                                    <div><strong>Department:</strong> {userDepartment || '—'}</div>
                                    <div><strong>Tenant:</strong> T178</div>
                                    <div><strong>L7 Manager:</strong> {leaderAlias}</div>
                                    <div><strong>Cost Center:</strong> {costCenter}</div>
                                    <div><strong>Job Family:</strong> {jobFamily}</div>
                                    <div><strong>Job Code:</strong> {jobCode}</div>
                                    <div><strong>Reports of:</strong> {reportsOf || '—'}</div>
                                </div>
                                <div style={{ marginTop: '10px', fontSize: '13px' }}><strong>Tool(s)/Function(s):</strong> {toolsRequested}</div>
                                <div style={{ marginTop: '6px', fontSize: '13px' }}><strong>Use Case:</strong> {useCase}</div>
                                <div style={{ marginTop: '6px', fontSize: '13px' }}><strong>Rule Detail:</strong> {ruleDetail}</div>
                                {usersLogins && <div style={{ marginTop: '6px', fontSize: '13px' }}><strong>Users:</strong> {usersLogins}</div>}
                            </div>

                            {/* Selected Permissions */}
                            <div style={{ padding: '12px 16px', borderRadius: '8px', background: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: '16px' }}>
                                <div style={{ fontSize: '13px', fontWeight: '600', color: '#166534', marginBottom: '6px' }}>Selected Permissions ({selectedPermissions.size})</div>
                                {selectedObjects.map(p => (
                                    <div key={p.id} style={{ fontSize: '12px', color: '#333', marginBottom: '2px' }}>
                                        • {p.name} <span style={{ color: '#666' }}>({p.riskType})</span>
                                    </div>
                                ))}
                            </div>


                            {submitError && <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#fef2f2', border: '1px solid #fecaca', fontSize: '13px', color: '#991b1b', marginBottom: '12px' }}>❌ {submitError}</div>}

                            {submitSuccess ? (
                                <div style={{ padding: '16px', borderRadius: '10px', background: '#f0fdf4', border: '2px solid #22c55e', textAlign: 'center' }}>
                                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>🎉</div>
                                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#166534', marginBottom: '4px' }}>Request Submitted Successfully!</div>
                                    <div style={{ fontSize: '13px', color: '#555' }}>Request ID: <strong>{submitSuccess.requestId}</strong></div>
                                    {submitSuccess.approvalsUrl && (
                                        <a href={submitSuccess.approvalsUrl} target="_blank" rel="noopener noreferrer"
                                            style={{ display: 'inline-block', marginTop: '10px', padding: '8px 16px', borderRadius: '6px', background: '#0073bb', color: '#fff', fontSize: '13px', fontWeight: '600', textDecoration: 'none' }}>
                                            View on Approvals ↗
                                        </a>
                                    )}
                                </div>
                            ) : (
                                <button type="button" onClick={async () => {
                                    setIsSubmitting(true);
                                    setSubmitError('');
                                    try {
                                        const { getAccessToken } = await import('../services/auth');
                                        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://b70682k0f3.execute-api.eu-north-1.amazonaws.com/prod';
                                        const token = getAccessToken();
                                        const headers = { 'Content-Type': 'application/json' };
                                        if (token) headers['Authorization'] = `Bearer ${token}`;
                                        
                                        const requestId = `PAM-RS-${Date.now().toString(36).toUpperCase()}`;
                                        const viewerUrl = `https://permissions-access-management.tse.amazon.dev/viewer?id=${requestId}`;
                                        
                                        const response = await fetch(`${API_BASE_URL}/validate`, {
                                            method: 'POST',
                                            headers,
                                            body: JSON.stringify({
                                                operation: 'createRulesetApproval',
                                                requestId,
                                                requesterAlias: userAlias,
                                                l7ManagerAlias: leaderAlias,
                                                title: `T178 Ruleset Access - ${selectedObjects.map(p => p.name).join(', ')}`,
                                                viewerUrl,
                                                formData: {
                                                    toolsRequested: toolsRequested || selectedObjects.map(p => p.name).join(', '),
                                                    costCenter,
                                                    usersLogins,
                                                    jobFamily,
                                                    jobCode,
                                                    reportsOf,
                                                    useCase,
                                                    ruleDetail,
                                                    selectedPermissionNames: selectedObjects.map(p => p.name)
                                                }
                                            })
                                        });
                                        
                                        const raw = await response.json();
                                        const result = typeof raw.body === 'string' ? JSON.parse(raw.body) : raw;
                                        
                                        if (!response.ok || result.error) {
                                            throw new Error(result.error || `API error: ${response.status}`);
                                        }
                                        
                                        setSubmitSuccess(result);
                                    } catch (err) {
                                        setSubmitError(err.message);
                                    } finally {
                                        setIsSubmitting(false);
                                    }
                                }} disabled={isSubmitting}
                                    style={{ width: '100%', padding: '16px', borderRadius: '8px', fontSize: '16px', fontWeight: '700', cursor: isSubmitting ? 'wait' : 'pointer', border: 'none', background: '#22c55e', color: '#fff' }}>
                                    {isSubmitting ? '⏳ Submitting...' : '🚀 Submit Ruleset Access Request'}
                                </button>
                            )}
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default RulesetRequest;
